import { ApiError, apiFetch } from '../api/http.ts';
import { rememberUser } from './lastKnownUser.ts';
import { setTokens, toTokenSet, type AuthTokenResponse } from './tokenStore.ts';

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  emailConfirmed: boolean;
  createdAt: string;
}

export interface RegisterInput {
  email: string;
  password: string;
  displayName: string;
  invitationToken?: string;
}

export interface RegisterResult {
  userId: string;
  requiresEmailConfirmation: boolean;
}

export async function register(input: RegisterInput): Promise<RegisterResult> {
  return apiFetch<RegisterResult>('/api/auth/register', {
    method: 'POST',
    json: input,
    anonymous: true,
  });
}

export async function signIn(email: string, password: string): Promise<void> {
  const response = await apiFetch<AuthTokenResponse>('/api/auth/login', {
    method: 'POST',
    json: { email, password },
    anonymous: true,
  });

  setTokens(toTokenSet(response));
}

export function signOut(): void {
  // No server call: bearer tokens are stateless and the refresh token becomes useless
  // the moment we forget it. Anything else would be theatre.
  setTokens(null);
  rememberUser(null);

  // Photographs are the one thing the service worker keeps that is a household's own
  // content rather than the app's shell, and it keys on URL rather than on who was
  // signed in. Unguessable ids mean nobody can reach one, but a shared phone should not
  // be relying on that — and this is a single line.
  void caches?.delete('item-images');
}

/**
 * Who is signed in.
 *
 * The only thing that ends a session is the *server* saying so. A request that never
 * arrived means the phone is in a chest freezer aisle, not that the account has gone — so
 * an unreachable server throws and leaves both the tokens and the remembered user alone,
 * and `AuthProvider` carries on with the latter. A 4xx is the opposite: the session is
 * dead, and keeping a stale identity on screen would mean an app that looks signed in and
 * 401s on everything it touches.
 */
export async function fetchCurrentUser(): Promise<CurrentUser> {
  try {
    const user = await apiFetch<CurrentUser>('/api/auth/me');
    rememberUser(user);

    return user;
  } catch (error) {
    if (error instanceof ApiError && error.isClientError) {
      setTokens(null);
      rememberUser(null);
    }

    throw error;
  }
}

export async function updateDisplayName(displayName: string): Promise<CurrentUser> {
  return apiFetch<CurrentUser>('/api/auth/me', { method: 'PUT', json: { displayName } });
}

export async function confirmEmail(userId: string, code: string): Promise<void> {
  const query = new URLSearchParams({ userId, code });
  await apiFetch<void>(`/api/auth/confirmEmail?${query.toString()}`, { anonymous: true });
}

export async function resendConfirmationEmail(email: string): Promise<void> {
  await apiFetch<void>('/api/auth/resendConfirmationEmail', {
    method: 'POST',
    json: { email },
    anonymous: true,
  });
}
